import { useEffect, useState, useCallback, useRef, useMemo } from 'react'
import { useAppSelector, useAppDispatch } from '../hooks'
import ColyseusRoomService from '../services/back4app/ColyseusRoomService'
import type { ColyseusRoomRecord } from '../../../types/Maps'
import { getPhaserGameSync } from '../PhaserGame'
import type Bootstrap from '../scenes/Bootstrap'
import { useCompanyDisabledState } from './useCompanyDisabledState'
import { discoveryService, DiscoveryRoom } from '../services/discoveryService'
import store from '../stores'
import { setDiscoveryRooms } from '../stores/RoomStore'

// Increased cache TTL for better performance with 100+ users
const CACHE_TTL = 120000 // 2 minutes instead of 30 seconds
const DEBOUNCE_DELAY = 3000 // 3 seconds debounce instead of 2 seconds
const REQUEST_TIMEOUT = 15000 // 15 seconds timeout for high-load scenarios
// After a failed fetch the next automatic one waits at least this long, doubling per failure up
// to the cap (SC-477). Without it every re-render refetched while Back4App was failing.
export const ROOM_LIST_BACKOFF_BASE_MS = 30_000
export const ROOM_LIST_BACKOFF_MAX_MS = 5 * 60_000

/** How long to wait after the n-th failure in a row (1 = first): 30 s, 60 s, 120 s ... capped. */
export function roomListBackoffMs(failures: number): number {
  const n = Math.max(1, Math.floor(failures))
  return Math.min(ROOM_LIST_BACKOFF_BASE_MS * 2 ** (n - 1), ROOM_LIST_BACKOFF_MAX_MS)
}

export interface OptimizedRoom {
  id: string
  name?: string
  description?: string
  mapId?: string
  isPublic?: boolean
  creatorName?: string
  // ...add other fields as needed
  [key: string]: any
}

const DISCOVERY_FALLBACK_DELAY_MS = 3000

/**
 * The rooms the menu lists (SC-422): every ColyseusRoom row of the company, running or not, with
 * the live player count when it runs. An on-demand room isn't running until someone joins, so
 * the running listings alone would hide it. A room that isn't running gets a placeholder id;
 * joining resolves it by `customRoomId`, which starts it. Running rooms without a row (the
 * public rooms) are kept as they are.
 *
 * `rows` is null until the company's rows have loaded. Until then the company's running rooms
 * are listed from their live metadata, so the lobby (always running) shows as soon as the
 * Colyseus lobby lists it instead of waiting on Back4App.
 */
export function mergeCompanyRooms(running: any[], rows: ColyseusRoomRecord[] | null): OptimizedRoom[] {
  if (!rows) {
    return running.map((r) =>
      r.metadata?.companyId && r.metadata?.customRoomId
        ? { ...r, isPublic: !r.metadata.hasPassword, metadata: { ...r.metadata, isDefaultRoom: true } }
        : r
    )
  }
  const rowIds = new Set(rows.map((r) => r.customRoomId))
  const fromRows = rows.map((row) => {
    const live = running.find((r) => (r.metadata?.customRoomId ?? r.customRoomId) === row.customRoomId)
    return {
      ...(live ?? {}),
      id: live?.id ?? live?.roomId ?? `room:${row.customRoomId}`,
      roomId: live?.roomId,
      name: row.name,
      description: row.description || '',
      mapId: row.mapId,
      isPublic: !row.hasPassword,
      clients: live?.clients ?? 0,
      customRoomId: row.customRoomId,
      metadata: {
        ...(live?.metadata ?? {}),
        name: row.name,
        description: row.description || '',
        customRoomId: row.customRoomId,
        companyId: row.companyId,
        mapId: row.mapId,
        isLobby: row.isLobby,
        persistent: row.persistent,
        priority: row.priority,
        hasPassword: row.hasPassword,
        // The menu lists company rooms in its "default rooms" section.
        isDefaultRoom: true,
      },
    }
  })
  const others = running.filter((r) => !rowIds.has(r.metadata?.customRoomId ?? r.customRoomId))
  return [...fromRows, ...others]
}

/**
 * Every company's rooms for the platform admins' "All Rooms" view: the `ColyseusRoom` rows,
 * marked running where a live room matches. Running rooms without a row (map editor rooms, the
 * public rooms) are left out.
 */
export function adminCompanyRooms(running: any[], rows: ColyseusRoomRecord[]): OptimizedRoom[] {
  const rowIds = new Set(rows.map((r) => r.customRoomId))
  return mergeCompanyRooms(running, rows).filter((room) => rowIds.has(room.metadata?.customRoomId))
}

export function useOptimizedRoomList(active: boolean, companyId?: string | null) {
  const dispatch = useAppDispatch()
  const colyseusRooms = useAppSelector((state) => state.room.availableRooms)
  const lobbyJoined = useAppSelector((state) => state.room.lobbyJoined)
  // null until this company's rows have loaded
  const [back4appRooms, setBack4appRooms] = useState<ColyseusRoomRecord[] | null>(null)
  const [loading, setLoading] = useState(false)
  const [lastFetched, setLastFetched] = useState(0)
  const [error, setError] = useState<string | null>(null)
  // Failures in a row, and when the next automatic fetch may run (0 = no back-off).
  const [retryCount, setRetryCount] = useState(0)
  const failuresRef = useRef(0)
  const [retryAt, setRetryAt] = useState(0)
  const [backoffTick, setBackoffTick] = useState(0)
  const refreshTimeout = useRef<NodeJS.Timeout | null>(null)
  const abortController = useRef<AbortController | null>(null)
  const discoveryFallbackAttempted = useRef<string | null>(null)
  const initialFetchDone = useRef<string | null>(null) // tracks companyId of the initial fetch

  // Check if company is disabled to avoid unnecessary polling
  const { isDisabled: isCompanyDisabled } = useCompanyDisabledState()

  // Subscribe to Colyseus lobby only when active and company is not disabled
  useEffect(() => {
    if (!active || isCompanyDisabled) {
      if (isCompanyDisabled && process.env.NODE_ENV !== 'production') {
        console.log('[useOptimizedRoomList] Skipping room list polling - company is disabled')
      }
      return
    }

    const phaserGame = getPhaserGameSync()
    if (!phaserGame) {
      // Phaser not yet initialized - this is normal during initial load
      console.log('[useOptimizedRoomList] Phaser not yet initialized, skipping lobby join')
      return
    }

    const bootstrap = phaserGame.scene?.keys?.bootstrap as Bootstrap | undefined
    if (bootstrap && (bootstrap as any).network) {
      // Wait for company hydration before joining lobby
      const initializeLobby = async () => {
        try {
          const hydrated = await (bootstrap as any).network.waitForCompanyHydrated(8000)
          if (hydrated) {
            console.log('[useOptimizedRoomList] Company hydrated, joining lobby')
            await (bootstrap as any).network.joinLobbyRoom()
          } else {
            console.warn('[useOptimizedRoomList] Company hydration timeout, joining lobby anyway')
            await (bootstrap as any).network.joinLobbyRoom()
          }
        } catch (error) {
          console.error('[useOptimizedRoomList] Failed to initialize lobby:', error)
        }
      }

      initializeLobby()
    }
    // No polling needed; Colyseus will emit events
    return () => {
      // Optionally leave lobby room if you want to save resources
      // if (bootstrap && (bootstrap as any).network) {
      //   (bootstrap as any).network.leaveLobbyRoom()
      // }
    }
  }, [active])

  // -----------------------------------------------------------------------
  // INITIAL LOAD FIX: Fetch rooms via the discovery REST endpoint as soon
  // as the hook becomes active, without waiting for Phaser / Colyseus lobby.
  // This ensures rooms are visible on the very first page load.
  //
  // Company ID priority:
  //  1. `companyId` prop — passed from MainMenu (most reliable source)
  //  2. Redux store userData.companyId — populated after login
  // -----------------------------------------------------------------------
  useEffect(() => {
    if (!active || isCompanyDisabled) return

    // Resolve company ID — prop first, then Redux store as fallback.
    const resolveCompanyId = (): string | null => {
      if (companyId && companyId.trim() !== '') return companyId
      try {
        const state = store.getState()
        return (
          (state as any)?.user?.userData?.companyId ??
          null
        )
      } catch {
        return null
      }
    }

    // Allow a short window for user / company state to hydrate.
    const timer = setTimeout(async () => {
      const resolvedId = resolveCompanyId()
      if (!resolvedId || resolvedId.trim() === '') return

      // Only fetch once per company to prevent redundant calls.
      if (initialFetchDone.current === resolvedId) return
      initialFetchDone.current = resolvedId

      // Skip if Colyseus already gave us rooms (e.g. fast lobby join).
      if (store.getState().room.availableRooms.length > 0) return

      console.log('[useOptimizedRoomList] Initial discovery fetch — companyId:', resolvedId)
      try {
        const discovery = await discoveryService.discoverCompanyRooms(resolvedId)
        if (discovery.success && discovery.rooms.length > 0) {
          // Only apply if rooms are still missing (Colyseus may have filled them).
          if (store.getState().room.availableRooms.length === 0) {
            const discoveryRooms = discovery.rooms.map((room: DiscoveryRoom) => ({
              roomId: room.roomId,
              name: room.name,
              description: room.metadata?.description || '',
              members: room.members,
              isPublic: !room.hasPassword,
              creatorName: room.metadata?.creatorName || 'Unknown',
              mapId: room.mapId,
              metadata: room.metadata,
              companyId: room.companyId,
              customRoomId: room.customRoomId,
            }))
            dispatch(setDiscoveryRooms(discoveryRooms as any))
            console.log(`[useOptimizedRoomList] Initial fetch dispatched ${discoveryRooms.length} rooms`)
          }
        }
      } catch (err) {
        console.warn('[useOptimizedRoomList] Initial discovery fetch failed:', err)
      }
    }, 500)

    return () => clearTimeout(timer)
    // Re-run when active changes or when the companyId prop arrives
    // (permissions load async, so companyId may be undefined on first render).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, isCompanyDisabled, companyId])

  // Discovery service integration for reconciliation
  // Always gets companyId from store if not provided
  const reconcileWithDiscovery = useCallback(async (companyId?: string | null) => {
    try {
      // Get companyId from parameter or from Redux store
      let resolvedCompanyId = companyId
      if (!resolvedCompanyId || resolvedCompanyId === '') {
        try {
          const state = (window as any).__REDUX_STORE__?.getState?.() ||
            ((global as any).store?.getState?.()) ||
            (typeof window !== 'undefined' && (window as any).store?.getState?.())
          if (state) {
            resolvedCompanyId = state?.user?.userData?.companyId ||
              state?.user?.company?.id ||
              null
          }
        } catch (e) {
          console.warn('[useOptimizedRoomList] Could not access store to get companyId:', e)
        }
      }

      if (!resolvedCompanyId || resolvedCompanyId.trim() === '') {
        console.warn('[useOptimizedRoomList] No companyId available - cannot discover rooms')
        return []
      }

      console.log('[useOptimizedRoomList] Reconciling with discovery service for company:', resolvedCompanyId)
      const discovery = await discoveryService.discoverCompanyRooms(resolvedCompanyId)

      if (discovery.success && discovery.rooms.length > 0) {
        console.log(`[useOptimizedRoomList] Discovery found ${discovery.rooms.length} rooms`)
        // Convert discovery rooms to the format expected by the store
        const discoveryRooms = discovery.rooms.map((room: DiscoveryRoom) => ({
          roomId: room.roomId,
          name: room.name,
          description: room.metadata?.description || '',
          members: room.members,
          isPublic: !room.hasPassword,
          creatorName: room.metadata?.creatorName || 'Unknown',
          mapId: room.mapId,
          metadata: room.metadata,
          companyId: room.companyId,
          customRoomId: room.customRoomId
        }))

        // Dispatch discovery rooms to store
        dispatch(setDiscoveryRooms(discoveryRooms as any))
        console.log('[useOptimizedRoomList] Discovery rooms dispatched to store')
        return discoveryRooms
      }

      return []
    } catch (error) {
      console.error('[useOptimizedRoomList] Discovery reconciliation failed:', error)
      return []
    }
  }, [dispatch])

  // Proactive discovery fallback when Colyseus rooms are empty after lobby join
  useEffect(() => {
    if (!active || isCompanyDisabled || !lobbyJoined) return
    if (colyseusRooms.length > 0) return

    const companyId =
      (typeof window !== 'undefined' &&
        ((window as any).__REDUX_STORE__?.getState?.()?.user?.userData?.companyId ||
          (window as any).__REDUX_STORE__?.getState?.()?.user?.company?.id)) ||
      null

    if (!companyId || companyId.trim() === '') return
    if (discoveryFallbackAttempted.current === companyId) return

    const timer = setTimeout(() => {
      const currentRooms = store.getState().room.availableRooms
      if (currentRooms.length > 0) return
      discoveryFallbackAttempted.current = companyId
      console.log('[useOptimizedRoomList] Rooms empty after lobby join, trying discovery fallback')
      reconcileWithDiscovery(companyId).catch(console.error)
    }, DISCOVERY_FALLBACK_DELAY_MS)

    return () => clearTimeout(timer)
  }, [active, isCompanyDisabled, lobbyJoined, colyseusRooms.length, reconcileWithDiscovery])

  // A different company is a different request: its first fetch must not wait on the last one's
  // failures or cache, and the last one's rows are not its rows.
  useEffect(() => {
    failuresRef.current = 0
    setRetryCount(0)
    setRetryAt(0)
    setLastFetched(0)
    setBack4appRooms(null)
  }, [companyId])

  // Fetch Back4App metadata only when needed with better error handling
  const fetchBack4appRooms = useCallback(
    async (options?: { fresh?: boolean }) => {
      if (loading) return // Prevent concurrent requests
      // No company yet: fetching would cache an empty list and hold back the real fetch for the
      // cache TTL once the company loads.
      if (!companyId) return

      setLoading(true)
      setError(null)

      // Cancel any ongoing request
      if (abortController.current) {
        abortController.current.abort()
      }

      // Create new abort controller
      abortController.current = new AbortController()

      try {
        // Only fetch required fields to reduce payload size
        // SC-422: the company's rooms are its ColyseusRoom rows, running or not.
        const result = await ColyseusRoomService.list(companyId, options)

        setBack4appRooms(result)
        const now = Date.now()
        setLastFetched(now)
        failuresRef.current = 0
        setRetryCount(0) // A success ends the back-off
        setRetryAt(0)

        // Emit event for MainMenu to update refresh feedback
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('roomsRefreshed', {
            detail: { timestamp: now, source: 'auto' }
          }))
        }
      } catch (error) {
        if (error instanceof Error && error.name === 'AbortError') {
          // Request was cancelled, don't treat as error
          return
        }

        console.error('Failed to fetch company rooms:', error)
        setError(error instanceof Error ? error.message : 'Failed to fetch rooms')

        // `lastFetched` stays as it was, so the cache still reads as stale: the back-off is what
        // keeps the effect below from fetching again on the next render.
        failuresRef.current += 1
        setRetryCount(failuresRef.current)
        setRetryAt(Date.now() + roomListBackoffMs(failuresRef.current))
      } finally {
        setLoading(false)
      }
    },
    [loading, companyId]
  )

  // Fetch on demand or if cache is stale (more stable)
  useEffect(() => {
    if (!active || isCompanyDisabled) return

    const now = Date.now()
    // Backing off after a failure: look again when it ends, not on every render.
    if (now < retryAt) {
      const timer = setTimeout(() => setBackoffTick((n) => n + 1), retryAt - now)
      return () => clearTimeout(timer)
    }

    const cacheAge = now - lastFetched

    // Check if we're in a cooldown period by listening to the global cooldown state
    const checkCooldown = () => {
      // Get cooldown state from MainMenu via custom event
      const cooldownEvent = new CustomEvent('checkRefreshCooldown', {
        detail: {
          callback: (inCooldown: boolean) => {
            // Only fetch if NOT in cooldown and cache is stale
            if (!inCooldown && cacheAge > CACHE_TTL && !loading) {
              fetchBack4appRooms()
            }
          }
        }
      })
      window.dispatchEvent(cooldownEvent)
    }

    // Only attempt to fetch if cache is significantly stale to prevent constant refreshing
    if (cacheAge > CACHE_TTL && !loading) {
      checkCooldown()
    }
  }, [active, isCompanyDisabled, lastFetched, loading, fetchBack4appRooms, retryAt, backoffTick])

  // Manual refresh (debounced and stable)
  const refresh = useCallback(() => {
    // Prevent multiple rapid refresh calls or refresh when company is disabled
    if (loading || isCompanyDisabled) return

    // Check cooldown before proceeding with refresh
    const checkCooldownAndRefresh = () => {
      const cooldownEvent = new CustomEvent('checkRefreshCooldown', {
        detail: {
          callback: (inCooldown: boolean) => {
            if (inCooldown) {
              console.log('Refresh blocked: cooldown active')
              return
            }

            if (refreshTimeout.current) {
              clearTimeout(refreshTimeout.current)
            }

            refreshTimeout.current = setTimeout(() => {
              // Only refresh if cache is stale or if explicitly requested
              const now = Date.now()
              if (now - lastFetched > CACHE_TTL || now - lastFetched > 10000) { // 10s minimum
                // The user asked: skip the 60 s list memo, and the back-off (it only holds back
                // automatic fetches).
                fetchBack4appRooms({ fresh: true })
              }
              refreshTimeout.current = null
            }, DEBOUNCE_DELAY)
          }
        }
      })
      window.dispatchEvent(cooldownEvent)
    }

    checkCooldownAndRefresh()
  }, [fetchBack4appRooms, loading, lastFetched, isCompanyDisabled])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (refreshTimeout.current) {
        clearTimeout(refreshTimeout.current)
      }
      if (abortController.current) {
        abortController.current.abort()
      }
    }
  }, [])

  const mergedRooms: OptimizedRoom[] = useMemo(
    () => mergeCompanyRooms(colyseusRooms as any[], back4appRooms),
    [colyseusRooms, back4appRooms]
  )

  return {
    rooms: mergedRooms,
    loading,
    error,
    refresh,
    retryCount
  }
} 